import { useEffect, useState } from "react";
import { get, size, map, groupBy, cloneDeep } from "lodash";
import { ChartSource } from "constant/chart";

const usePanelConfiguration = ({ panelData, deviceList }: any) => {
    const [defaultValue, setDefaultValue] = useState<any>({
        multipleChartType: "same",
        chart_name: "",
        data: {},
    });

    useEffect(() => {
        const attributeList = get(panelData, "attributes", []).map(
            (item: any) => {
                const device = deviceList?.find(
                    (device: any) =>
                        device.deviceId === item.device_id + "/" + item.said
                );

                return {
                    key: device?.deviceId,
                    ...device,
                    ...item,
                };
            }
        );

        const isCorrelationChart =
            panelData?.source === ChartSource.SENSORS_AND_ACTUATORS;

        // For correlation charts, group by sensor/actuator column (0 = sensors, 1 = actuators)
        // For other charts, group by device type name
        if (isCorrelationChart) {
            const sensorDevices: any = {};
            const actuatorDevices: any = {};
            let sensorType = "";
            let actuatorType = "";

            attributeList.forEach((item: any) => {
                const key = `${item.device_id}/${item.said}`;
                if (item.isActuator) {
                    actuatorDevices[key] = item;
                    if (!actuatorType) actuatorType = item.name || "";
                } else {
                    sensorDevices[key] = item;
                    if (!sensorType) sensorType = item.name || "";
                }
            });

            const hasSensors = Object.keys(sensorDevices).length > 0;
            const hasActuators = Object.keys(actuatorDevices).length > 0;

            setDefaultValue({
                multipleChartType:
                    hasSensors && hasActuators ? "different" : "same",
                chart_name: get(panelData, "name", ""),
                data: {
                    0: {
                        sensor_type: sensorType,
                        devices: sensorDevices,
                    },
                    1: {
                        sensor_type: actuatorType,
                        devices: actuatorDevices,
                    },
                },
            });
        } else {
            const deviceTypes = groupBy(attributeList, "name");
            const multipleChartType =
                size(deviceTypes) > 1 ? "different" : "same";
            const deviceData = map(deviceTypes, (value, key) => {
                const uniqueDevices = value.reduce((acc, curr): any => {
                    const key = `${curr.device_id}/${curr.said}`;
                    if (key) {
                        acc[`${key}`] = curr;
                    }
                    return acc;
                }, {});
                return {
                    sensor_type: key === "undefined" ? value[0].name : key,
                    devices: { ...uniqueDevices },
                };
            });

            const allDeviceData = cloneDeep(deviceData).reduce(
                (acc: any, curr: any) => {
                    const existingSensorType = acc.find(
                        ({ sensor_type }: { sensor_type: string }) =>
                            sensor_type === curr.sensor_type
                    );
                    if (existingSensorType) {
                        existingSensorType.devices = {
                            ...existingSensorType.devices,
                            ...curr.devices,
                        };
                    } else {
                        acc.push(curr);
                    }
                    return acc;
                },
                []
            );

            setDefaultValue({
                multipleChartType,
                chart_name: get(panelData, "name", ""),
                data: { ...allDeviceData },
            });
        }
    }, [deviceList, panelData]);

    return { defaultValue };
};

export default usePanelConfiguration;
